import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ShieldCheck } from 'lucide-react';
import { assessmentService, consentService, paymentService } from '../services';
import Questionnaire from '../components/Questionnaire';
import DinSpecIntro from '../components/DinSpecIntro';
import SelfAssessmentDisclaimerGate from '../components/SelfAssessmentDisclaimerGate';
import Layout from '../components/Layout';
import { Badge, Card, Button } from '../components/ds';
import { useIsMobile } from '../hooks/useMediaQuery';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const statusMap = {
  draft:       { label: 'Entwurf',        tone: 'neutral' },
  in_progress: { label: 'In Bearbeitung', tone: 'warning' },
  completed:   { label: 'Abgeschlossen',  tone: 'secure' },
};

const AssessmentPage = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  // Self-Assessment-Disclaimer (AUD-14): { version, text, accepted }. Nur im
  // Draft-Zustand relevant — blockt den Start, bis der Nutzer bestätigt hat.
  const [disclaimer, setDisclaimer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // KI-Assistent ist ein Paid-Feature (AUD-56): im Free-Tier ausgeblendet, das
  // Backend (/api/ai) erzwingt es zusätzlich.
  const [hasAccess, setHasAccess] = useState(false);
  const isMobile = useIsMobile();

  const loadAssessment = useCallback(async () => {
    try {
      const res = await assessmentService.getAssessment(assessmentId);
      setAssessment(res.data);
      // Nur beim Start (Draft) nötig; laufende/abgeschlossene Audits brauchen kein Gate.
      if (res.data.status === 'draft') {
        const d = await consentService.getSelfAssessmentDisclaimer();
        setDisclaimer(d.data);
      }
    } catch {
      setError('Assessment konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { loadAssessment(); }, [loadAssessment]);

  useEffect(() => {
    let cancelled = false;
    paymentService.getSubscription()
      .then((res) => { if (!cancelled) setHasAccess(Boolean(res.data?.hasAccess)); })
      .catch(() => { if (!cancelled) setHasAccess(false); });
    return () => { cancelled = true; };
  }, []);

  const handleComplete = async () => {
    await loadAssessment();
    setTimeout(() => navigate('/dashboard'), 1800);
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#8A93A3', fontSize: 14, fontFamily: FONT }}>
          Lädt…
        </div>
      </Layout>
    );
  }

  if (error || !assessment) {
    return (
      <Layout>
        <div style={{
          background: '#FDF1F1', border: '1px solid #FADEDF',
          borderRadius: 10, padding: '16px 20px',
          color: '#9A1C22', fontSize: 14, fontFamily: FONT,
        }}>{error || 'Assessment nicht gefunden.'}</div>
      </Layout>
    );
  }

  const s = statusMap[assessment.status] || statusMap.draft;

  return (
    <Layout>
      <div style={{ padding: isMobile ? '20px 16px 32px' : '36px 40px 48px', fontFamily: FONT }}>
      {/* Breadcrumb + title row */}
      <div style={{ marginBottom: 28 }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#667085', fontSize: 13, fontFamily: FONT,
            padding: 0, marginBottom: 14,
          }}
        >
          <ArrowLeft width={14} height={14} />
          Zurück zum Dashboard
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#101623', letterSpacing: '-0.011em' }}>
              {assessment.title}
            </h1>
            {assessment.description && (
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#667085' }}>
                {assessment.description}
              </p>
            )}
          </div>
          <div style={{ marginTop: 4, flexShrink: 0 }}>
            <Badge tone={s.tone}>{s.label}</Badge>
          </div>
        </div>
      </div>

      {/* Content by status */}
      {assessment.status === 'completed' && (
        <Card padding={0} style={{ maxWidth: 480, margin: '0 auto' }}>
          <div style={{ padding: '56px 40px', textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: '#DFF3E7', color: '#17864B',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <Check width={28} height={28} strokeWidth={2.5} />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#101623' }}>
              Assessment abgeschlossen
            </h2>
            <p style={{ color: '#667085', fontSize: 14, margin: '0 0 28px', lineHeight: 1.6 }}>
              Ihre Antworten wurden gespeichert. Sie können nun zum Dashboard zurückkehren.
            </p>
            <Button onClick={() => navigate('/dashboard')} size="md">Zum Dashboard</Button>
          </div>
        </Card>
      )}

      {/* Blocking-Gate (AUD-14): Erst nach bestätigtem Disclaimer erscheint der Start. */}
      {assessment.status === 'draft' && disclaimer && !disclaimer.accepted && (
        <SelfAssessmentDisclaimerGate
          disclaimer={disclaimer}
          onAccepted={() => setDisclaimer((d) => ({ ...d, accepted: true }))}
        />
      )}

      {assessment.status === 'draft' && disclaimer?.accepted && assessment.templateId === 'din_spec_27076' && (
        <DinSpecIntro
          onStart={async () => {
            await assessmentService.updateStatus(assessmentId, 'in_progress');
            await loadAssessment();
          }}
        />
      )}

      {assessment.status === 'draft' && disclaimer?.accepted && assessment.templateId !== 'din_spec_27076' && (
        <Card padding={0} style={{ maxWidth: 480, margin: '0 auto' }}>
          <div style={{ padding: '52px 40px', textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16,
              background: '#F2F6FE', color: '#1E4FBE',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <ShieldCheck width={30} height={30} />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#101623' }}>
              Bereit loszulegen?
            </h2>
            <p style={{ color: '#667085', fontSize: 14, margin: '0 0 28px', lineHeight: 1.6 }}>
              Dieses Assessment bewertet die IT-Sicherheitslage Ihres Unternehmens.
              Ihre Antworten werden automatisch gespeichert.
            </p>
            <Button
              size="md"
              onClick={async () => {
                await assessmentService.updateStatus(assessmentId, 'in_progress');
                await loadAssessment();
              }}
            >Assessment starten →</Button>
          </div>
        </Card>
      )}

      {assessment.status === 'in_progress' && (
        <Questionnaire
          assessmentId={assessmentId}
          templateId={assessment.templateId}
          onComplete={handleComplete}
          enableAi={hasAccess}
        />
      )}
      </div>
    </Layout>
  );
};

export default AssessmentPage;
